import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './UploadStatus.css';

interface MissingUpload {
  warehouse: string;
  year: number;
  month: number;
  monthName: string;
}

interface UploadStatusData {
  warehouse: string;
  hasUpload: boolean;
  fileCount: number;
}

interface MonthSummary {
  year: number;
  month: number;
  monthName: string;
  totalWarehouses: number;
  uploadedWarehouses: number;
  missingWarehouses: string[];
}

const UploadStatus: React.FC = () => {
  const [currentMonthValidation, setCurrentMonthValidation] = useState<{
    isComplete: boolean;
    missing: MissingUpload[];
    currentMonth: string;
    totalWarehouses: number;
  } | null>(null);
  const [uploadSummary, setUploadSummary] = useState<MonthSummary[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [monthStatus, setMonthStatus] = useState<{
    year: number;
    month: number;
    warehouses: UploadStatusData[];
    missing: MissingUpload[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      
      
      

      
      const mockValidation = {
        currentMonth: 'Декември 2024',
        isComplete: false,
        totalWarehouses: 3,
        missing: [
          { warehouse: 'Склад 1', year: 2024, month: 12, monthName: 'Декември' },
          { warehouse: 'Склад 3', year: 2024, month: 12, monthName: 'Декември' }
        ]
      };
      const mockSummary = [
        {
          year: 2024,
          month: 12,
          monthName: 'Декември',
          totalWarehouses: 3,
          uploadedWarehouses: 1,
          missingWarehouses: ['Склад 1', 'Склад 3']
        }
      ];

      setCurrentMonthValidation(mockValidation);
      setUploadSummary(mockSummary);
    } catch (err: any) {
      console.error('Error fetching upload data:', err);
      setError(err.response?.data?.message || 'Failed to fetch upload data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthStatus = useCallback(async () => {
    try {
      const response = await axios.get(`/api/v1/upload/status/${selectedYear}/${selectedMonth}`);
      setMonthStatus(response.data);
    } catch (err: any) {
      console.error('Error fetching month status:', err);
      setError(err.response?.data?.message || 'Failed to fetch month status');
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    if (selectedYear && selectedMonth) {
      fetchMonthStatus();
    }
  }, [selectedYear, selectedMonth, fetchMonthStatus]);

  const getStatusColor = (hasUpload: boolean) => {
    return hasUpload ? '#28a745' : '#dc3545';
  };

  const getCompletionPercentage = (uploaded: number, total: number) => {
    return total > 0 ? Math.round((uploaded / total) * 100) : 0;
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => currentYear - 1 + i);
  const months = [
    { value: 1, name: 'Януари' }, { value: 2, name: 'Февруари' }, { value: 3, name: 'Март' },
    { value: 4, name: 'Април' }, { value: 5, name: 'Май' }, { value: 6, name: 'Юни' },
    { value: 7, name: 'Юли' }, { value: 8, name: 'Август' }, { value: 9, name: 'Септември' },
    { value: 10, name: 'Октомври' }, { value: 11, name: 'Ноември' }, { value: 12, name: 'Декември' }
  ];

  if (loading) {
    return (
      <div className="upload-status">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Зареждане на статуса на качванията...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-status">
      <div className="status-header">
        <h2>Контролен Панел за Статус на Качванията</h2>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {}
      {currentMonthValidation && (
        <div className="current-month-status">
          <h3>Статус на Текущия Месец</h3>
          <div className={`status-card ${currentMonthValidation.isComplete ? 'complete' : 'incomplete'}`}>
            <div className="status-icon">
              {currentMonthValidation.isComplete ? '✅' : '⚠️'}
            </div>
            <div className="status-content">
              <h4>{currentMonthValidation.currentMonth}</h4>
              <p>
                {currentMonthValidation.isComplete 
                  ? 'Всички складове са качили своите файлове' 
                  : `${currentMonthValidation.missing.length} складове не са качили файлове`
                }
              </p>
              {!currentMonthValidation.isComplete && (
                <div className="missing-warehouses">
                  <strong>Липсват:</strong> {currentMonthValidation.missing.map(m => m.warehouse).join(', ')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {}
      <div className="month-selector">
        <h3>Проверете Конкретен Месец</h3>
        <div className="selector-row">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {}
      {monthStatus && (
        <div className="month-status">
          <h3>{months.find(m => m.value === monthStatus.month)?.name} {monthStatus.year}</h3>
          <div className="warehouses-grid">
            {monthStatus.warehouses.map((warehouse) => (
              <div key={warehouse.warehouse} className="warehouse-card">
                <div className="warehouse-header">
                  <h4>{warehouse.warehouse}</h4>
                  <div 
                    className="status-indicator"
                    style={{ backgroundColor: getStatusColor(warehouse.hasUpload) }}
                  >
                    {warehouse.hasUpload ? '✓' : '✗'}
                  </div>
                </div>
                <div className="warehouse-details">
                  <p>Статус: {warehouse.hasUpload ? 'Качен' : 'Липсва'}</p>
                  {warehouse.hasUpload && (
                    <p>Файлове: {warehouse.fileCount}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {}
      <div className="upload-summary">
        <h3>Обобщение за Последните 6 Месеца</h3>
        <div className="summary-grid">
          {uploadSummary.map((month) => (
            <div key={`${month.year}-${month.month}`} className="summary-card">
              <div className="summary-header">
                <h4>{month.monthName} {month.year}</h4>
                <div className="completion-badge">
                  {getCompletionPercentage(month.uploadedWarehouses, month.totalWarehouses)}%
                </div>
              </div>
              <div className="summary-details">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ 
                      width: `${getCompletionPercentage(month.uploadedWarehouses, month.totalWarehouses)}%` 
                    }}
                  ></div>
                </div>
                <p>{month.uploadedWarehouses} / {month.totalWarehouses} складове</p>
                {month.missingWarehouses.length > 0 && (
                  <div className="missing-list">
                    <strong>Липсват:</strong> {month.missingWarehouses.join(', ')}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UploadStatus; 